import { getAppRoot } from './../identity';
import { postServiceRequest } from './../serviceAgent';
import { expireSessionAndRedirect } from './../aaa';
import {
  getAuthorizationToken,
  getClassroomGameProgress,
  isMixpanelQueryOnlyReportsEnabled,
  queryMixpanelByGroupV2,
} from '../BackendInterface';
import { getUsersEmail } from '../GlobalFunctions';
import associationData from './Associations.json';
import cx from 'classnames';
import chartStyles from '../AdministratorDashboard/MoodboardElementaryMobile/ChartSegment.module.scss';
import {
  ActivityInfo,
  ComprehensionDataPoint,
  ProgressData,
  StruggleDataPoint,
  StudentProgressData,
} from '@peekapak/types/PeekavilleGameDataTypes';
import { StudentProxyType } from '@peekapak/types/DataProtocolTypes';
import { AxiosAugmentedError, ErrorType } from '../AugmentedError';
import {
  aggregateClassroomProgressByStudent,
  getClassroomGameProgressScript,
  getGameProgressQueryStartDate,
} from '../AdministratorDashboard/reportUtilities';

export interface RequestPayload {
  userId: string;
  email: string;
  classroomName: string;
}

// get day 1 - 7
export function getDayOfWeekFrom(createdAt: number) {
  const d = new Date(createdAt);
  return d.getDay();
}

export const dropdownOptions = [
  { text: 'Self-Regulation', value: 1 },
  { text: 'Respect', value: 2 },
  { text: 'Gratitude', value: 3 },
  { text: 'Perseverance', value: 4 },
  { text: 'Empathy', value: 5 },
  { text: 'Teamwork', value: 6 },
  { text: 'Kindness', value: 7 },
  { text: 'Honesty', value: 8 },
  { text: 'Optimism', value: 9 },
  { text: 'Courage', value: 10 },
  { text: 'Specials', value: 0 },
];

export const getClassGameData = async (
  classroomId: string,
  districtId: string,
  schoolId: string,
  tableOverride?: string,
) => {
  const useMixpanel = await isMixpanelQueryOnlyReportsEnabled(districtId, schoolId);

  if (useMixpanel) {
    //
    // NEW PATH: query Mixpanel for all game progress events for this classroom.
    // Struggle and comprehension data are embedded in the returned events,
    // eliminating per-student DynamoDB calls in PeekavilleProgressReport.
    //
    try {
      const today = new Date().toISOString().split('T')[0];
      const fromDate = getGameProgressQueryStartDate('2023-08-01');
      const script = getClassroomGameProgressScript(classroomId, fromDate, today);
      const events = await queryMixpanelByGroupV2(script, 'unified');
      const results: StudentProgressData[] = aggregateClassroomProgressByStudent(events);
      return { output: results };
    } catch (error) {
      if ((error as AxiosAugmentedError).code === ErrorType.UNAUTHORIZED) {
        expireSessionAndRedirect();
      } else {
        throw error;
      }
    }
  } else {
    //
    // LEGACY PATH: DynamoDB classroom game progress.
    // Retained for rollback capability behind the feature flag.
    //
    try {
      const results: StudentProgressData[] = await getClassroomGameProgress(classroomId, tableOverride);
      return {
        output: results,
      };
    } catch (error) {
      if ((error as AxiosAugmentedError).code === ErrorType.UNAUTHORIZED) {
        expireSessionAndRedirect();
      } else {
        throw error;
      }
    }
  }
};

export const getStudentLists = async (progressData: ProgressData, studentList: StudentProxyType[]) => {
  const studentsWithData: StudentProxyType[] = [];
  const studentsWithoutData: StudentProxyType[] = [];

  // The Mixpanel path returns entries with firstName/lastName as empty strings
  // and only userIdHash populated. Detect this and match by userId instead of
  // name to avoid placing all students in studentsWithoutData on that path.
  const firstEntry = progressData.output[0];
  const useIdMatch = Boolean(firstEntry && firstEntry.userIdHash && !firstEntry.firstName);

  studentList?.reduce((_, curr): null => {
    const studentHasData = useIdMatch
      ? progressData.output.find((el) => el.userIdHash === curr.userId)
      : progressData.output.find(
          (el) =>
            `${el.firstName} ${el.lastName}`.replace(/\s+/g, '') ===
            `${curr.firstName} ${curr.lastName || ''}`.replace(/\s+/g, ''),
        );
    studentHasData ? studentsWithData.push(curr) : studentsWithoutData.push(curr);
    return null;
  }, null);
  return { studentsWithData, studentsWithoutData };
};

export const compileStudentData = (
  studentPeekavilleData: StudentProgressData['gameData']['progress'],
  struggleData: StruggleDataPoint[],
  comprehensionData: ComprehensionDataPoint[],
) => {
  const progressWithStruggleData =
    struggleData.length === 0
      ? studentPeekavilleData
      : studentPeekavilleData.map((item) => ({
          ...item,
          struggleData:
            item.type === 'book'
              ? comprehensionData.filter(
                  (dataPoint) => item.id.includes(dataPoint.book.toString()) && item.readingLevel === dataPoint.level,
                )
              : struggleData.filter((dataPoint) => dataPoint.gameId === item.id),
        }));
  const studentUnitData = compileUnitData(progressWithStruggleData);
  return studentUnitData;
};

export const compileUnitData = (combinedProgressStruggleData: StudentProgressData['gameData']['progress']) => {
  const association: { [key: string]: ActivityInfo } = associationData.associations;
  const units = getUnitDisplayInfo();
  const progressEntries = combinedProgressStruggleData.length;
  for (let j = 0; j < progressEntries; j++) {
    const idValue = combinedProgressStruggleData[j].id;
    if (!(idValue in association)) {
      console.warn(`Could not find this association ${idValue}`);
    } else {
      const sequenceIndex = association[idValue].unitSequence;
      const unitIndex = association[idValue].unit;
      units[unitIndex][sequenceIndex] = {
        ...combinedProgressStruggleData[j],
        ...units[unitIndex][sequenceIndex],
      };
    }
  }
  return units;
};

export const getUnitDisplayInfo = () => {
  // get the unit display array from Associations
  const units = [];
  for (let i = 0; i < 11; i++) {
    const unit: ActivityInfo[] | null = [];
    const associations: { [key: string]: ActivityInfo } = associationData.associations;
    for (const key in associations) {
      if (associations[key].unit === i) {
        const unitInfo = {
          displayName: associations[key].displayName,
          casel: associations[key].casel,
          type: associations[key].type,
          description: associations[key].description,
          levels: associations[key].levels,
          unit: associations[key].unit,
          unitSequence: associations[key].unitSequence,
        };
        unit.push(unitInfo);
      }
    }
    const removeDuplicatesUnit = [...new Map(unit.map((m) => [m.displayName, m])).values()];
    const unitToDisplay = removeDuplicatesUnit.sort((a, b) => a.unitSequence - b.unitSequence);
    units.push(unitToDisplay);
  }

  return units;
};

/*******************************************************

                    MOOD FUNCTIONS

*******************************************************/

export const moodMap = ['No check-in', 'Happy', 'Excited', 'Sad', 'Angry', 'Calm', 'Nervous'];
export const moodToNumberMap = {
  'No check-in': 0,
  Happy: 1,
  Excited: 2,
  Sad: 3,
  Angry: 4,
  Calm: 5,
  Nervous: 6,
};

export const generateJournalData = (outputArray) => {
  const journalData = [];
  // populate the renderable journalData with explicit journal entries
  outputArray.forEach((studentInfo) => {
    const values = Object.values(studentInfo.journals);
    const student = {};
    values.forEach((journalEntry) => {
      const dayOfWeek = getDayOfWeekFrom(journalEntry.createdAt);
      const prevEntry = student[`day${dayOfWeek}`];
      // check if an existing entry is newer and should not be overwritten
      if (prevEntry && journalEntry.createdAt < prevEntry[1]) return;
      student[`day${dayOfWeek}`] = [moodMap[journalEntry.feeling], journalEntry.createdAt];
    });
    journalData.push(student);
  });
  return journalData;
};

export const colorMap = {
  Calm: ['#FFE0D1', '#54595D', 'Y'],
  Sad: ['#C0E8F2', '#54595D', 'B'],
  'No check-in': ['#F2F2F2', '#54595D', 'N'],
  Happy: ['#FF7735', '#54595D', 'Y'],
  Excited: ['#FF5305', '#54595D', 'Y'],
  Angry: ['#00A3D3', '#54595D', 'B'],
  Nervous: ['#1EC5EA', '#54595D', 'B'],
};

export const generateBarChart = (barChartData) => {
  if (!barChartData) return null;
  const outputChartElement = [];

  let idx = 0;

  for (const key in barChartData) {
    const studentNum = barChartData[key][0] + barChartData[key][1] + barChartData[key][2];
    const allNeutral = barChartData[key][0] === 0 && barChartData[key][2] === 0;
    const hasPositive = barChartData[key][0] > 0 && barChartData[key][2] === 0;
    const hasNegative = barChartData[key][2] > 0 && barChartData[key][0] === 0;
    const hasPositiveAndNegative = barChartData[key][0] > 0 && barChartData[key][2] > 0;
    const positiveHeight = 100 * (barChartData[key][0] / studentNum);
    const neutralHeight = 100 * (barChartData[key][1] / studentNum);
    const negativeHeight = 100 - positiveHeight - neutralHeight;
    const positiveTop = -neutralHeight / 2 - positiveHeight + 50;
    outputChartElement.push(
      <div key={(idx += 1)} className={chartStyles.elementPlaceholder}>
        <div className={chartStyles.positiveElement} style={{ top: `${positiveTop}%`, height: `${positiveHeight}%` }} />
        <div
          className={cx(chartStyles.neutralElement, {
            [chartStyles.fullBorderRadius]: allNeutral,
            [chartStyles.noBorderRadius]: hasPositiveAndNegative,
            [chartStyles.bottomBorderRadius]: hasPositive,
            [chartStyles.topBorderRadius]: hasNegative,
          })}
          style={{ top: `${positiveTop}%`, height: `${neutralHeight}%` }}
        />
        <div className={chartStyles.negativeElement} style={{ top: `${positiveTop}%`, height: `${negativeHeight}%` }} />
      </div>,
    );
  }
  return outputChartElement;
};

export const generateBarChartData = (tableData, startOfWeekUnix = 0) => {
  const msInADay = 60 * 60 * 24 * 1000;
  const outputData = [];
  if (!tableData || tableData.length === 0) return null;
  for (let i = 0; i < 7; i += 1) {
    let yellowCount = 0;
    let greyCount = 0;
    let blueCount = 0;
    let date = '';
    tableData.forEach((entry) => {
      date = `day${i}`;
      if (!entry[date]) {
        const createdAt = startOfWeekUnix + msInADay * i;
        entry[date] = ['No check-in', createdAt];
      }
      if (colorMap[entry[date][0]][2] === 'Y') {
        yellowCount += 1;
      } else if (colorMap[entry[date][0]][2] === 'B') {
        blueCount += 1;
      } else if (colorMap[entry[date][0]][2] === 'N') {
        greyCount += 1;
      }
    });
    outputData[date] = [yellowCount, greyCount, blueCount];
  }
  return outputData;
};
